import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCcw } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { EmptyAssessmentState } from "@/components/layout/EmptyAssessmentState";
import { Button } from "@/components/ui/button";
import { ManualReviewBanner } from "@/components/results/ManualReviewBanner";
import { CategoryCard } from "@/components/results/CategoryCard";
import { ClassifierOutputCard } from "@/components/results/ClassifierOutputCard";
import { SourceVerificationPanel } from "@/components/results/SourceVerificationPanel";
import { ReadyDomainSummary } from "@/components/results/ReadyDomainSummary";
import { EvidenceReadinessTable } from "@/components/results/EvidenceReadinessTable";
import { EvidenceFilteredList } from "@/components/results/EvidenceFilteredList";
import { ResultsRows } from "@/components/results/ResultsRows";
import { ClarificationRequiredList } from "@/components/results/ClarificationRequiredList";
import { ActionPlanTimeline } from "@/components/results/ActionPlanTimeline";
import { CopyableSummary } from "@/components/results/CopyableSummary";
import { SaveToServerButton } from "@/components/results/SaveToServerButton";
import { useAssessment } from "@/context/AssessmentContext";
import { buildPlainTextSummary, clarificationRequiredItems } from "@/lib/assessmentLogic";
import { isServerSaveAvailable } from "@/lib/backendClient";
import { formatDate } from "@/lib/timeUtils";

const CONTACT_EMAIL = "hello@spiralbeyondcompliance.com.au";
const SCOPED_SUPPORT_MAILTO =
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Request scoped readiness support \u2014 NDIS 0138 Readiness")}`;

export default function ResultsPage() {
  const navigate = useNavigate();
  const { answers, result, completedAt, assessmentId, resetAssessment } = useAssessment();

  const generatedAt = useMemo(() => formatDate(completedAt), [completedAt]);

  const summaryText = useMemo(() => {
    if (!result) return "";
    return buildPlainTextSummary({
      result,
      answers,
      assessmentId: assessmentId || "\u2014",
      generatedAt
    });
  }, [result, answers, assessmentId, generatedAt]);

  if (!completedAt || !result) {
    return (
      <EmptyAssessmentState
        sectionLabel="\u00a7 03 \u00b7 Snapshot"
        pageTitle="No readiness snapshot yet"
        message="The Snapshot is generated automatically when you complete the readiness intake. Once the intake is complete, this page will display your provider category, R.E.A.D.Y. summary, evidence map, operational risk, and 30 / 60 / 90 day action plan."
      />
    );
  }

  const handleRestart = () => {
    resetAssessment();
    navigate("/");
  };

  return (
    <PageShell>
      <div className="container py-10 lg:py-14">
        <div className="flex flex-col gap-2">
          <p className="eyebrow">§ 03 · Output</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display text-3xl font-medium leading-tight text-foreground sm:text-4xl">
              Preliminary readiness snapshot
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                asChild
                variant="ghost"
                className="rounded-sm px-3 text-[13px] font-medium text-foreground hover:bg-secondary"
              >
                <Link to="/intake">
                  <ArrowLeft className="h-4 w-4" />
                  Edit answers
                </Link>
              </Button>
              <Button
                asChild
                className="rounded-sm px-4 text-[13px] font-medium tracking-wide shadow-paper"
              >
                <Link to="/report">
                  Open client report
                </Link>
              </Button>
              <SaveToServerButton />
              <Button
                type="button"
                variant="outline"
                onClick={handleRestart}
                className="rounded-sm border-border bg-card px-3 text-[13px] font-medium hover:bg-secondary"
              >
                <RefreshCcw className="h-4 w-4" />
                Start new assessment
              </Button>
            </div>
          </div>
          <p className="max-w-3xl text-base leading-relaxed text-muted-foreground">
            NDIS 0138 Readiness is a consultancy readiness tool only. It is not legal advice, an
            Approved Quality Auditor report, an official NDIS Commission tool, a registration
            guarantee, or a compliance certification.
          </p>
          {isServerSaveAvailable() && (
            <p
              data-testid="save-to-server-privacy-note"
              className="max-w-3xl rounded-sm border border-border/60 bg-muted/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground"
            >
              Save to Server stores a de-identified provider record on the NDIS 0138 Readiness consultancy
              backend. No participant data, contact details, or free-text notes are transmitted.
            </p>
          )}
        </div>

        <div className="mt-8 space-y-6">
          {result.manualReview && (
            <ManualReviewBanner reasons={result.manualReviewReasons} />
          )}

          <CategoryCard
            category={result.category}
            manualReview={result.manualReview}
            assessmentId={assessmentId || "—"}
            generatedAt={generatedAt}
          />

          <ClassifierOutputCard classifier={result.classifier} />

          <SourceVerificationPanel verification={result.sourceVerification} />

          <ReadyDomainSummary domains={result.domainRollup} />

          <EvidenceReadinessTable domains={result.domainRollup} />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EvidenceFilteredList
              eyebrow="§ 06 · Red flags"
              title="Missing evidence"
              accent="red"
              items={result.missingEvidence}
              emptyLabel="No missing evidence recorded."
            />
            <EvidenceFilteredList
              eyebrow="§ 07 · Amber flags"
              title="Evidence needing review"
              accent="amber"
              items={result.reviewEvidence}
              emptyLabel="No items flagged for review."
            />
          </div>

          <ClarificationRequiredList items={clarificationRequiredItems(result)} />

          <ResultsRows
            eyebrow="§ 08 · A — Operational risk"
            title="Operational risk summary"
            rows={result.operationalRisks}
            emptyLabel="No operational risk responses recorded."
          />

          <ActionPlanTimeline plan={result.actionPlan} />

          <section
            className="rounded-sm border border-border bg-card p-6 sm:p-8"
            data-testid="results-commercial-continuation"
          >
            <h2 className="font-display text-2xl font-medium leading-tight text-foreground sm:text-3xl">
              If you want to go further
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground/90">
              Your preliminary readiness snapshot identifies where further review may be useful. Detailed evidence review, provider-specific findings and implementation planning are delivered as a scoped professional service through Spiral Beyond Compliance.
            </p>
            <div className="mt-6">
              <Button asChild className="rounded-sm px-5 text-[13px] font-medium tracking-wide shadow-paper">
                <a href={SCOPED_SUPPORT_MAILTO} data-testid="request-scoped-support-cta">
                  Request scoped readiness support
                </a>
              </Button>
            </div>
          </section>

          <CopyableSummary summaryText={summaryText} assessmentId={assessmentId} />
        </div>
      </div>
    </PageShell>
  );
}
